import { useContext, useEffect } from 'react';
import {
 Switch, Route, Redirect, useLocation,
} from 'react-router-dom';

import './index.scss';
import { delayImport, menus } from 'utils';
import {
 Header, Sidebar, ScrollbarsIf, Spinner,
} from 'components';

import {
    ROOT_URL,
    HOME_URL,
    SEARCH_URL,
    APPOINTMENT_URL,
    ADD_APPOINTMENT_URL,
    VIEW_APPOINTMENT_URL,
    EDIT_APPOINTMENT_URL,
    VIEW_INVOICE_URL,
    REBOOK_APPOINTMENT_URL,
    SALES_URL,
    SALES_DAILY_SALES_URL,
    SALES_APPOINTMENTS_URL,
    SALES_INVOICES_URL,
    STAFFS_URL,
    CLIENTS_URL,
    ADD_CLIENT_URL,
    VIEW_CLIENT_URL,
    EDIT_CLIENT_URL,
    SERVICES_URL,
    LISTINGS_URL,
    COMBO_URL,
    ADD_COMBO_URL,
    VIEW_COMBO_URL,
    DEBT_URL,
    REVENUE_URL,
    CHECKIN_OUT_URL,
    REGIMEN_URL,
    REPORT_URL,
    LANDING_PAGE_URL,
    DISCOUNT_URL,
    CUSTOMER_EXPERIENCE_URL,
    OVERVIEW_URL,
    SMART_MARKETING_URL,
    BRANCH_URL,
    LOYALTY_URL,
    SETTINGS_URL,
    MY_PROFILE_URL,
    NOT_FOUND_URL,
    APPOINTMENT_CHECKOUT_URL,
    COMBO_CHECKOUT_URL,
    PRODUCT_CHECKOUT_URL,
    INVOICE_CHECKOUT_URL,
    ADD_COMBO_APPOINTMENT_URL,
    EDIT_COMBO_APPOINTMENT_URL,
} from 'actions';
import {
 AuthContext, ComboContext, CustomerContext, HomeContext,
} from 'contexts';

const Home = delayImport(import('pages/Dashboard/Home'));
const Search = delayImport(import('pages/Dashboard/Search'));
const Appointment = delayImport(import('pages/Dashboard/Appointment'));
const AppointmentList = delayImport(import('pages/Dashboard/Appointment/AppointmentList'));
const AddAppointment = delayImport(import('pages/Dashboard/Appointment/AddAppointment'));
const ViewAppointment = delayImport(import('pages/Dashboard/Appointment/ViewAppointment'));
const Checkout = delayImport(import('pages/Dashboard/Checkout'));
const Invoices = delayImport(import('pages/Dashboard/Invoices'));
const ViewInvoice = delayImport(import('pages/Dashboard/Invoices/ViewInvoice'));
const DailySales = delayImport(import('pages/Dashboard/DailySales'));
const RebookAppointment = delayImport(import('pages/Dashboard/Appointment/RebookAppointment'));
const Staffs = delayImport(import('pages/Dashboard/Staffs'));
const Clients = delayImport(import('pages/Dashboard/Clients'));
const AddClient = delayImport(import('pages/Dashboard/Clients/AddClient'));
const ViewClient = delayImport(import('pages/Dashboard/Clients/ViewClient'));
const Services = delayImport(import('pages/Dashboard/Services'));
const Listings = delayImport(import('pages/Dashboard/Listings'));
const Combos = delayImport(import('pages/Dashboard/Combos'));
const AddCombo = delayImport(import('pages/Dashboard/Combos/AddCombo'));
const ViewCombo = delayImport(import('pages/Dashboard/Combos/ViewCombo'));
const Debts = delayImport(import('pages/Dashboard/Debts'));
const Revenue = delayImport(import('pages/Dashboard/Revenue'));
const CheckInOut = delayImport(import('pages/Dashboard/CheckInOut'));
const Regimen = delayImport(import('pages/Dashboard/Regimen'));
const Report = delayImport(import('pages/Dashboard/Report'));
const LandingPage = delayImport(import('pages/Dashboard/LandingPage'));
const Discount = delayImport(import('pages/Dashboard/Discount'));
const CustomerExperience = delayImport(import('pages/Dashboard/CustomerExperience'));
const Overview = delayImport(import('pages/Dashboard/Overview'));
const SmartMarketing = delayImport(import('pages/Dashboard/SmartMarketing'));
const Branchs = delayImport(import('pages/Dashboard/Branchs'));
const Loyalty = delayImport(import('pages/Dashboard/Loyalty'));
const Settings = delayImport(import('pages/Dashboard/Settings'));
const MyProfile = delayImport(import('pages/Dashboard/MyProfile'));

const HomeLayout = () => {
    const location = useLocation();
    const titles = [
        HOME_URL,
        APPOINTMENT_URL,
        SALES_URL,
        STAFFS_URL,
        CLIENTS_URL,
        SERVICES_URL,
        COMBO_URL,
        DEBT_URL,
        REVENUE_URL,
        CHECKIN_OUT_URL,
        REGIMEN_URL,
        REPORT_URL,
        LISTINGS_URL,
        LANDING_PAGE_URL,
        DISCOUNT_URL,
        CUSTOMER_EXPERIENCE_URL,
        OVERVIEW_URL,
        SMART_MARKETING_URL,
        BRANCH_URL,
        LOYALTY_URL,
        SETTINGS_URL,
        MY_PROFILE_URL,
    ];
    const { userInfoLoading } = useContext(HomeContext);
    const { loggedInUser, doGetUserInfo } = useContext(AuthContext);
    const { totalCombos } = useContext(ComboContext);
    const { totalCustomers } = useContext(CustomerContext);

    /*eslint-disable */
    useEffect(() => {
        if (loggedInUser) doGetUserInfo();
    }, [loggedInUser]);
    /* eslint-enable */

    const checkSidebarAvailable = () => {
        const exceptions = [
            ADD_APPOINTMENT_URL,
            VIEW_APPOINTMENT_URL.replace('/:appointmentId', '/'),
            EDIT_APPOINTMENT_URL.replace('/:appointmentId', '/'),
            EDIT_COMBO_APPOINTMENT_URL.replace('/:comboId/:appointmentId', '/'),
            ADD_COMBO_APPOINTMENT_URL.replace('/:comboId', '/'),
            APPOINTMENT_CHECKOUT_URL,
            COMBO_CHECKOUT_URL,
            PRODUCT_CHECKOUT_URL,
            INVOICE_CHECKOUT_URL,
            VIEW_INVOICE_URL.replace('/:invoiceId', '/'),
            ADD_CLIENT_URL,
            EDIT_CLIENT_URL.replace('/:clientId', ''),
            ADD_COMBO_URL,
            VIEW_COMBO_URL.replace('/:comboId', '/'),
            SEARCH_URL,
        ];
        const excepts = [REBOOK_APPOINTMENT_URL.replace('/:appointmentId', '/')];
        return exceptions.some((e) => {
            const isException = location.pathname.indexOf(`${e.slice(1, e.length)}`) > -1;
            const isExcept = !excepts.some((ex) => location.pathname.indexOf(`${ex.slice(1, ex.length)}`) > -1);
            const isEqual = location.pathname === e;
            return (isException && isExcept) || isEqual;
        });
    };

    return (
        <div className="spa__home-layout d-flex">
            {userInfoLoading && <Spinner transparent />}
            {!checkSidebarAvailable() && <Sidebar />}
            <div className="spa__home-layout--content">
                {!checkSidebarAvailable() && (
                    <Switch>
                        {titles.map((t) => (
                            <Route path={t} exact={t === HOME_URL} key={t}>
                                <Header
                                    title={t === MY_PROFILE_URL
                                        ? 'Trang cá nhân'
                                        : `
                                            ${(menus.find((m) => m.url === t)?.label as string)}
                                            ${location.pathname === COMBO_URL ? ` (${totalCombos})` : ''}
                                            ${location.pathname === CLIENTS_URL ? ` (${totalCustomers})` : ''}
                                        `}
                                />
                            </Route>
                        ))}
                    </Switch>
                )}
                <div className="spa__home-layout--content--main">
                    <ScrollbarsIf enable={!checkSidebarAvailable() && location.pathname !== '/lich-hen'}>
                        <Switch>
                            <Route path={ROOT_URL} exact>
                                <Redirect to={APPOINTMENT_URL} />
                            </Route>
                            <Route path={HOME_URL} exact component={Home} />
                            <Route path={SEARCH_URL} exact component={Search} />
                            <Route path={APPOINTMENT_URL} exact component={Appointment} />
                            <Route path={ADD_APPOINTMENT_URL} exact component={AddAppointment} />
                            <Route path={VIEW_APPOINTMENT_URL} exact component={ViewAppointment} />
                            <Route path={EDIT_COMBO_APPOINTMENT_URL} exact component={AddAppointment} />
                            <Route path={EDIT_APPOINTMENT_URL} exact component={AddAppointment} />
                            <Route path={ADD_COMBO_APPOINTMENT_URL} exact component={AddAppointment} />
                            <Route path={APPOINTMENT_CHECKOUT_URL} component={Checkout} />
                            <Route path={COMBO_CHECKOUT_URL} component={Checkout} />
                            <Route path={PRODUCT_CHECKOUT_URL} component={Checkout} />
                            <Route path={INVOICE_CHECKOUT_URL} component={Checkout} />
                            <Route path={VIEW_INVOICE_URL} exact component={ViewInvoice} />
                            <Route path={REBOOK_APPOINTMENT_URL} exact component={RebookAppointment} />
                            <Route path={SALES_URL} exact>
                                <Redirect to={SALES_DAILY_SALES_URL} />
                            </Route>
                            <Route path={SALES_DAILY_SALES_URL} exact component={DailySales} />
                            <Route path={SALES_APPOINTMENTS_URL} component={AppointmentList} />
                            <Route path={SALES_INVOICES_URL} exact component={Invoices} />
                            <Route path={STAFFS_URL} exact component={Staffs} />
                            <Route path={CLIENTS_URL} exact component={Clients} />
                            <Route path={ADD_CLIENT_URL} exact component={AddClient} />
                            <Route path={VIEW_CLIENT_URL} exact component={ViewClient} />
                            <Route path={EDIT_CLIENT_URL} exact component={AddClient} />
                            <Route path={SERVICES_URL} exact component={Services} />
                            <Route path={LISTINGS_URL} exact component={Listings} />
                            <Route path={COMBO_URL} exact component={Combos} />
                            <Route path={ADD_COMBO_URL} exact component={AddCombo} />
                            <Route path={VIEW_COMBO_URL} exact component={ViewCombo} />
                            <Route path={DEBT_URL} exact component={Debts} />
                            <Route path={REVENUE_URL} exact component={Revenue} />
                            <Route path={CHECKIN_OUT_URL} exact component={CheckInOut} />
                            <Route path={REGIMEN_URL} exact component={Regimen} />
                            <Route path={REPORT_URL} component={Report} />
                            <Route path={LANDING_PAGE_URL} component={LandingPage} />
                            <Route path={DISCOUNT_URL} exact component={Discount} />
                            <Route path={CUSTOMER_EXPERIENCE_URL} exact component={CustomerExperience} />
                            <Route path={OVERVIEW_URL} exact component={Overview} />
                            <Route path={SMART_MARKETING_URL} exact component={SmartMarketing} />
                            <Route path={BRANCH_URL} component={Branchs} />
                            <Route path={LOYALTY_URL} exact component={Loyalty} />
                            <Route path={SETTINGS_URL} exact component={Settings} />
                            <Route path={MY_PROFILE_URL} component={MyProfile} />
                            <Route path="*">
                                <Redirect to={NOT_FOUND_URL} />
                            </Route>
                        </Switch>
                    </ScrollbarsIf>
                </div>
            </div>
        </div>
    );
};

export default HomeLayout;
